import { FilterCriteria } from '../../constants';
import { checkIsBooleanField, getValueTypesByField } from '../../utils';
import { FilterTokenizer } from './FilterTokenizer';
import { FilterParser } from './FilterParser';
import { FilterSemanticValidator } from './FilterSemanticValidator';

export interface TokenInfo {
  fields: string[];
  arithmeticOperators: string[];
  values: any[];
  logicalOperators: string[];
}

export function extractTokenInfo(
  value: string,
  filterOptions: Record<string, FilterCriteria>
): TokenInfo {
  const { tokens } = FilterTokenizer.tokenize(value);

  const fields: string[] = [];
  const arithmeticOperators: string[] = [];
  const values: any[] = [];
  const logicalOperators: string[] = [];

  tokens.forEach((token) => {
    switch (token.type) {
      case 'field':
        fields.push(token.value.toLowerCase());

        // Handle boolean fields - add placeholders like the original function
        if (
          checkIsBooleanField(getValueTypesByField(token.value, filterOptions))
        ) {
          arithmeticOperators.push('BOOL');
          values.push(true);
        }
        break;

      case 'operator':
        arithmeticOperators.push(token.value);
        break;

      case 'value':
        values.push(token.value);
        break;

      case 'logical':
        logicalOperators.push(token.value);
        break;
    }
  });

  return {
    fields,
    arithmeticOperators,
    values,
    logicalOperators,
  };
}

export function validateFilterSyntax(
  value: string,
  filterOptions: Record<string, FilterCriteria>
): { isValid: boolean; errorMessage?: string } {
  if (!value || value.trim().length === 0) {
    return { isValid: false, errorMessage: 'Definition is required' };
  }

  // Use the new FilterParser with semantic validation
  const parser = new FilterParser(filterOptions);
  const parseResult = parser.parse(value);

  // Check syntax errors first
  if (parseResult.errors.length > 0) {
    const firstError = parseResult.errors[0];
    return { isValid: false, errorMessage: firstError.message };
  }

  // Check semantic validation first (if available), then syntax completeness
  if (
    parseResult.semanticValidation &&
    !parseResult.semanticValidation.isValid
  ) {
    const firstSemanticError = parseResult.semanticValidation.errors[0];

    // Create a user-friendly error message
    let errorMessage = firstSemanticError.message;

    // Add suggestions if available
    if (
      firstSemanticError.suggestions &&
      firstSemanticError.suggestions.length > 0
    ) {
      const suggestions = firstSemanticError.suggestions.slice(0, 3).join(', ');
      errorMessage += ` Did you mean: ${suggestions}?`;
    }

    return { isValid: false, errorMessage };
  }

  // If semantic validation passed, check if filter is syntactically complete
  if (!parseResult.isValid) {
    return { isValid: false, errorMessage: 'Incomplete filter expression' };
  }

  return { isValid: true };
}
